import { useEffect, useCallback } from 'react'

interface UseModalOptions {
  /** Whether the modal is currently open */
  isOpen: boolean
  /** Callback to close the modal */
  onClose: () => void
  /** Whether pressing Escape should close the modal (default: true) */
  closeOnEscape?: boolean
  /** Whether to prevent body scroll when modal is open (default: true) */
  preventBodyScroll?: boolean
}

/**
 * Hook to handle modal behavior:
 * - Close on Escape key press
 * - Prevent body scroll when modal is open
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const [isOpen, setIsOpen] = useState(false)
 *   useModal({ isOpen, onClose: () => setIsOpen(false) })
 *
 *   return isOpen ? <div className="modal">...</div> : null
 * }
 * ```
 */
export function useModal({
  isOpen,
  onClose,
  closeOnEscape = true,
  preventBodyScroll = true,
}: UseModalOptions) {
  // Handle Escape key press
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose()
      }
    },
    [onClose, closeOnEscape]
  )

  // Add/remove event listeners and manage body scroll
  useEffect(() => {
    if (isOpen) {
      // Add escape key listener
      document.addEventListener('keydown', handleEscape)

      // Prevent body scroll
      let originalOverflow: string | undefined
      if (preventBodyScroll) {
        originalOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
        if (preventBodyScroll && originalOverflow !== undefined) {
          document.body.style.overflow = originalOverflow
        }
      }
    }
  }, [isOpen, handleEscape, preventBodyScroll])
}

/**
 * Creates a handler for backdrop click that closes the modal
 * when clicking outside the modal content.
 *
 * Usage:
 * ```tsx
 * const handleBackdropClick = createBackdropClickHandler(onClose)
 *
 * return (
 *   <div className="backdrop" onClick={handleBackdropClick}>
 *     <div className="modal-content">...</div>
 *   </div>
 * )
 * ```
 */
export function createBackdropClickHandler(onClose: () => void) {
  return (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop, not its children
    if (e.target === e.currentTarget) {
      onClose()
    }
  }
}

export default useModal
